/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Document numbering — the pure rules for the customer-facing document family.
 *
 * Format: `{L}{KIND}-{YYYY}-{NNNN}[{X}]` — entity letter (W / S / P), a
 * two-letter kind (QT, OC, IN, WO, DO), four-digit zero-padded serial, and an
 * optional ordinal suffix (second document on the same order = `A`, third =
 * `B`, …). Spec: docs/superpowers/specs/2026-09-09-entity-prefixed-document-numbering-design.md.
 *
 * The OC number IS the order's family serial. The invoice, work orders and
 * deliveries DERIVE from it (`deriveDocumentNumber`), so the year is the
 * ORDER's year, never the creation year. Quotes keep their own serial.
 *
 * Shared with the Cloud Functions — keep it pure and
 * structurally typed (no imports from src/types). This is the single source
 * of every format/parse rule for both the web app and the Cloud Functions.
 */

export type IssuingCompanyName = 'WSS Windows & Doors' | 'Smart Renovation' | 'Pinki Beans';
export type EntityLetter = 'W' | 'S' | 'P';
export type FamilyKind = 'QT' | 'OC' | 'IN' | 'WO' | 'DO';
/** The kinds whose number is derived from the order's OC serial. */
export type DerivedKind = 'IN' | 'WO' | 'DO';

const KNOWN_COMPANIES: ReadonlySet<string> = new Set<IssuingCompanyName>([
  'WSS Windows & Doors',
  'Smart Renovation',
  'Pinki Beans',
]);

/** Unknown / legacy / absent → WSS, the historical hardcode (was inline in
 *  promoteQuoteToOrder; moved here so the client and every CF agree). */
export const resolveIssuingCompany = (raw: unknown): IssuingCompanyName =>
  typeof raw === 'string' && KNOWN_COMPANIES.has(raw)
    ? (raw as IssuingCompanyName)
    : 'WSS Windows & Doors';

const LETTER_BY_COMPANY: Record<IssuingCompanyName, EntityLetter> = {
  'WSS Windows & Doors': 'W',
  'Smart Renovation': 'S',
  'Pinki Beans': 'P',
};

export const entityLetter = (company: unknown): EntityLetter =>
  LETTER_BY_COMPANY[resolveIssuingCompany(company)];

/** Allocator counter kind → family display kind. Counter keys never change
 *  (`sequences/QTE-2026` keeps counting); only the printed prefix does. */
export const COUNTER_KIND_TO_FAMILY = { QTE: 'QT', OC: 'OC' } as const;

const PAD_TO = 4;

/** 1 → '' (first document is bare), 2 → 'A', 3 → 'B', … 27 → 'Z'. */
export const ordinalSuffix = (ordinal: number): string => {
  if (!Number.isInteger(ordinal) || ordinal < 1 || ordinal > 27) {
    throw new Error(`Cannot spell document ordinal ${ordinal}.`);
  }
  return ordinal === 1 ? '' : String.fromCharCode(64 + ordinal - 1);
};

export const formatFamilyNumber = (a: {
  letter: EntityLetter;
  kind: FamilyKind;
  year: number;
  serial: number;
  ordinal?: number;
}): string =>
  `${a.letter}${a.kind}-${a.year}-${String(a.serial).padStart(PAD_TO, '0')}${ordinalSuffix(
    a.ordinal ?? 1,
  )}`;

// Legacy `OC-2026-0009` and lettered `WOC-2026-0042` both parse; the serial
// may exceed four digits (never truncated).
const OC_NUMBER = /^(?:[WSP])?OC-(\d{4})-(\d{4,})$/;

export const parseOrderSerial = (ocNumber: unknown): { year: number; serial: number } | null => {
  if (typeof ocNumber !== 'string') return null;
  const m = OC_NUMBER.exec(ocNumber);
  if (!m) return null;
  return { year: Number(m[1]), serial: Number(m[2]) };
};

/** Invoice / WO / DO number for an order. Null when the order carries no
 *  parsable OC number — the caller must allocate one first (ensureOrderSerial). */
export const deriveDocumentNumber = (
  kind: DerivedKind,
  order: { ocNumber?: unknown; issuingCompany?: unknown },
  ordinal: number,
): string | null => {
  const parsed = parseOrderSerial(order.ocNumber);
  if (!parsed) return null;
  return formatFamilyNumber({
    letter: entityLetter(order.issuingCompany),
    kind,
    year: parsed.year,
    serial: parsed.serial,
    ordinal,
  });
};

/** A DRAFT quote that switches entity keeps its serial and swaps its letter.
 *  Legacy `QTE-` numbers (and UUID-slice refs) pass through unchanged. */
export const reletterQuoteNumber = (number: string, letter: EntityLetter): string =>
  number.replace(/^[WSP](?=QT-)/, letter);
